import { useMemo } from 'react';

import { SearchEventsRequest } from '../../../audit-trail/gen/ai/h2o/audittrail/v1/event_service_pb';
import { getFilterDefinition } from '../config/filterRegistry';

interface UseAuditTrailQueryMappingProps {
  filters: Record<string, any>;
  allWorkspaces: boolean;
  ACTIVE_WORKSPACE_NAME: string;
  showReadOnlyEvents: boolean;
}

/**
 * Hook that maps filter state to backend API request parameters.
 * Uses the filter registry to automatically map filters to the correct API fields.
 */
export const useAuditTrailQueryMapping = ({
  filters,
  allWorkspaces,
  ACTIVE_WORKSPACE_NAME,
  showReadOnlyEvents,
}: UseAuditTrailQueryMappingProps) => {
  // Map filter state to API request using registry configuration
  return useMemo(() => {
    const request: SearchEventsRequest = { pageSize: 50 };
    request.filter = {};

    // Map filters to request based on registry
    Object.entries(filters).forEach(([filterKey, { value }]) => {
      const filterDef = getFilterDefinition(filterKey);
      if (!filterDef || !value) return;

      switch (filterDef.type) {
        case 'dateTime':
          if (value.dateStart) request.filter!.startEventTime = value.dateStart;
          if (value.dateEnd) request.filter!.endEventTime = value.dateEnd;

          break;
        case 'string':
          if (filterDef.requestMapping.direct) (request.filter as any)[filterDef.requestMapping.direct] = value;

          break;
        case 'regex':
          if (value.inputData) {
            if (value.isRegEx && filterDef.requestMapping.regex) {
              (request.filter as any)[filterDef.requestMapping.regex] = value.inputData;
            } else if (!value.isRegEx && filterDef.requestMapping.exact) {
              (request.filter as any)[filterDef.requestMapping.exact] = value.inputData;
            }
          }

          break;
        case 'boolean':
          if (filterDef.requestMapping.direct) (request.filter as any)[filterDef.requestMapping.direct] = value;

          break;
        case 'metadata':
          if (filterDef.requestMapping.direct) (request.filter as any)[filterDef.requestMapping.direct] = value;

          break;
      }
    });

    if (!allWorkspaces) request.filter.workspaceExact = ACTIVE_WORKSPACE_NAME;
    else delete request.filter.workspaceExact;

    if (!showReadOnlyEvents) request.filter.readOnly = false;
    else delete request.filter.readOnly;

    return request;
  }, [filters, allWorkspaces, ACTIVE_WORKSPACE_NAME, showReadOnlyEvents]);
};
